import React, { useState, useEffect } from 'react'
import dayjs from 'dayjs'
import { useAuth, useUser } from '@clerk/clerk-react';
import { useSelector, useDispatch } from 'react-redux'
import { Link, useNavigate } from 'react-router-dom'
import { SignedIn, SignedOut, RedirectToSignIn, UserButton } from "@clerk/clerk-react";
import { 
  CalendarDays, 
  CalendarCheck,
  ListChecks,
  Menu,
  PanelRightOpen,
  PanelLeftOpen,
  Plus,
  Shield,
} from 'lucide-react'

import PrinceGeoffrey from './Chat/PrinceGeoffrey'
import TaskSetupModal from './Task/TaskSetupModal'
import TaskCategoryIcon from './Shared/TaskCategoryIcon'
import { setCurrentTask, createTask } from '../reducers/taskReducer'
import { updateTaskService } from '../utils/taskService'
import { isAdminAppUser } from '../utils/responsePartnerUtils'


export default function Sidebar() {
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const { getToken, userId } = useAuth();
  const { user: clerkUser } = useUser();
  const isAdmin = isAdminAppUser(clerkUser);

  const { tasks = [] } = useSelector((state) => state.taskList || { tasks: [] });
  const { digests = [] } = useSelector((state) => state.digestList || { digests: [] });
  const currentTask = useSelector((state) => state.taskList?.currentTask);
  const { projectCategories } = useSelector((state) => state.taskCategoryList || { projectCategories: [] })

  const [, setSelectedTask] = useState(currentTask?.id)

  const [isSidebarOpen, setIsSidebarOpen] = useState(true)
  const [showTaskSetup, setShowTaskSetup] = useState(false)
  const [pendingTask, setPendingTask] = useState(null)
  const [windowWidth, setWindowWidth] = useState(window.innerWidth)
  const [isMenuOpen, setIsMenuOpen] = useState(false)
  const [fullTaskListVisible, setFullTaskListVisible] = useState(false)
  
  // Handle window resize
  useEffect(() => {
    const handleResize = () => {
      setWindowWidth(window.innerWidth)
      // Auto-collapse sidebar on small screens
      if (window.innerWidth < 768) { // 768px is typical md breakpoint
        setIsSidebarOpen(false)
      } else {
        setIsSidebarOpen(true)
      }
    }
    
    window.addEventListener('resize', handleResize)
    handleResize() // Initialize on mount
    
    return () => window.removeEventListener('resize', handleResize)
  }, [])

  // Window Sizes
  const getIsDesktopOnly = () => {
    return windowWidth >= 1024
  }
  const getIsTabletOnly = () => {
    return windowWidth < 1024 && windowWidth > 768
  }
  const getIsMobileOnly = () => {
    return windowWidth < 768
  }

  const handleCreateNewTask = async () => {
    const newTask = {
      id: `task_${Math.random().toString(36).substring(2, 15)}`,
      title: 'Untitled Task',
      description: '',
      startBy: 0,
      endBy: 0,
      estimation: 0,
      category: ['task'],
      completed: false,
      archived: false,
      metaData: {}
    }

    setPendingTask(newTask)
    dispatch(setCurrentTask(newTask.id))
    setShowTaskSetup(true)
    if (windowWidth < 768) setIsMenuOpen(false)
  }

  const handleTaskUpdate = (updatedTask) => {
    setPendingTask(updatedTask)
  }

  const handleSaveTask = async () => {
    if (pendingTask) {
      try {
        const savedTask = await updateTaskService(getToken, userId, pendingTask)

        dispatch(createTask(savedTask))
        dispatch(setCurrentTask(null))
        setPendingTask(null)
        setShowTaskSetup(false)
      } catch (error) {
        console.error('Error saving task:', error)
      }
    }
  }

  const handleCancelTask = async () => {
    dispatch(setCurrentTask(null))
    setPendingTask(null)
    setShowTaskSetup(false)
  }

  const toggleMobileMenu = () => {
    setIsMenuOpen(!isMenuOpen)
  };

  const handleDailyDigestClick = () => {
    setSelectedTask(null)
    navigate('/digest')
    if (getIsMobileOnly()) setIsMenuOpen(false)
  };

  const handleViewAllTasksClick = () => {
    // setSelectedTask(null)
    setIsMenuOpen(false)
  }

  const getDigestIsoDate = (digest) => {
    if (digest?.title) {
      const parsedTitle = dayjs(digest.title);
      if (parsedTitle.isValid()) {
        return parsedTitle.format('YYYY-MM-DD');
      }
    }

    const fallbackDate = digest?.updated_at || digest?.created_at;
    if (fallbackDate) {
      const parsedFallback = dayjs(fallbackDate);
      if (parsedFallback.isValid()) {
        return parsedFallback.format('YYYY-MM-DD');
      }
    }

    return null;
  }

  const getDigestDisplayTitle = (digest, isoDate) => {
    if (digest?.title) return digest.title;
    if (isoDate) return dayjs(isoDate).format('MMMM D, YYYY');
    return 'Digest';
  }

  const todayIsoDate = dayjs().format('YYYY-MM-DD');
  const recentDigests = digests
    .map(digest => ({ digest, isoDate: getDigestIsoDate(digest) }))
    .filter(({ isoDate }) => isoDate && isoDate !== todayIsoDate)
    .sort((a, b) => dayjs(b.isoDate).valueOf() - dayjs(a.isoDate).valueOf())
    .slice(0, 5);

  const RenderSideMenu = () => {
    return (
      <aside className="flex flex-col w-full h-full overflow-y-auto">
        {((getIsDesktopOnly() && isSidebarOpen) || isMenuOpen) && (
          <div className="flex flex-row items-center justify-between py-4 px-6 border-b">
            
            <div className='flex flex-row items-center justify-start gap-3'>
              <PrinceGeoffrey size={26} className='justify-center items-center m-0 p-0' />
              {!getIsMobileOnly() && <h1 className="text-base text-slate-700 font-bold ml-1 mt-2">Geoffrey AI</h1>}
            </div>
            
            <button className="m-0 mt-2 p-0 rounded-lg bg-transparent text-black hover:bg-transparent hover:text-sky-500"
              data-amp-track-section="Sidebar"
              data-amp-track-action="Sidebar_Toggle_Click"
              data-amp-track-description="Collapse Sidebar"
              onClick={() => {
                setIsSidebarOpen(false)
                setIsMenuOpen(false)
              }}
            >
              <PanelRightOpen size={22} />
            </button>
          </div>
        )}
        {getIsDesktopOnly() && !isSidebarOpen && (
          <div className="flex flex-col items-center py-4 border-b">
            <PrinceGeoffrey size={26} className='justify-center items-center m-0 p-0' />
            <button className="m-0 mt-5 p-0 bg-transparent text-slate-700 hover:bg-transparent hover:text-sky-500"
              data-amp-track-section="Sidebar"
              data-amp-track-action="Sidebar_Toggle_Click"
              data-amp-track-description="Expand Sidebar"
              onClick={() => {
                setIsSidebarOpen(true)
                setIsMenuOpen(false)
              }}
              >
                <PanelLeftOpen size={22} />
            </button>
          </div>
        )}
        {getIsTabletOnly() && (
          <div className="flex flex-col items-center p-4 border-b">
            <PrinceGeoffrey size={26} />
          </div>
        )}

        <div className="flex flex-col w-full p-4 border-b gap-1 border-b-slate-300">
          <button
            className={`flex flex-row items-center font-semibold text-slate-700 ${!isMenuOpen && (getIsTabletOnly() || !isSidebarOpen) ? 'justify-center' : 'justify-start'} p-2 m-0 gap-4 bg-transparent hover:bg-sky-500 hover:text-white`}
            data-amp-track-section="Sidebar"
            data-amp-track-action="Daily_Digest_Click"
            data-amp-track-description="Open Daily Digest"
            onClick={() => handleDailyDigestClick()}
          >
            <CalendarCheck size={26} />
            <span className={`${!isMenuOpen && (getIsTabletOnly() || !isSidebarOpen) ? 'sr-only' : ''}`}>Today's Digest</span>
          </button>
          
          {/* TODO: Add Dashboard, Notifications, Summon Assistant */}
          {/* <button
            className={`flex flex-row items-center font-semibold text-slate-700 ${!isMenuOpen && (getIsTabletOnly() || !isSidebarOpen) ? 'justify-center' : 'justify-start'} p-2 m-0 gap-4 bg-transparent hover:bg-sky-500 hover:text-white`}
            onClick={handleDashboardClick}
          >
            <LayoutDashboard size={26} />
            <span className={`${!isMenuOpen && (getIsTabletOnly() || !isSidebarOpen) ? 'sr-only' : ''}`}>Dashboard</span>
          </button>
          <button
            className={`flex flex-row items-center font-semibold text-slate-700 ${!isMenuOpen && (getIsTabletOnly() || !isSidebarOpen) ? 'justify-center' : 'justify-start'} p-2 m-0 gap-4 bg-transparent hover:bg-sky-500 hover:text-white`}
            onClick={handleNotificationsClick}
          >
            <Bell size={26} />
            <span className={`${!isMenuOpen && (getIsTabletOnly() || !isSidebarOpen) ? 'sr-only' : ''}`}>Notifications</span>
          </button>
          <button
            className={`flex flex-row items-center font-semibold text-slate-700 ${!isMenuOpen && (getIsTabletOnly() || !isSidebarOpen) ? 'justify-center' : 'justify-start'} p-2 m-0 gap-4 bg-transparent hover:bg-sky-500 hover:text-white`}
            onClick={handleSummonAssistantClick}
          >
            <WandSparkles size={26} />
            <span className={`${!isMenuOpen && (getIsTabletOnly() || !isSidebarOpen) ? 'sr-only' : ''}`}>Summon Assistant</span>
          </button> */}
        </div>
        
        <div className="flex flex-col w-full p-4 border-b gap-1 border-b-slate-300">
          <div
            className={`flex flex-row font-semibold gap-4 w-full items-center p-2 m-0 text-slate-700 ${!isMenuOpen && (getIsTabletOnly() || !isSidebarOpen) ? 'justify-center' : 'justify-start'}`}
          >
            <CalendarDays size={24} />
            {isMenuOpen ? 'Recent Digests' : (!getIsTabletOnly() && isSidebarOpen) ? 'Recent Digests' : ''}
          </div>

          {(getIsMobileOnly() || (!getIsTabletOnly() && isSidebarOpen)) && recentDigests.length > 0 && (
            <nav>
              <ul className="mt-2 px-2">
                {recentDigests.map(({ digest, isoDate }) => (
                  <li key={digest.id || isoDate}>
                    <Link 
                      to={`/digest/${isoDate}`} 
                      data-amp-track-section="Sidebar"
                      data-amp-track-action="View_Digest_Click"
                      data-amp-track-description="View Digest"
                      onClick={() => {
                        setSelectedTask(null)
                        setIsMenuOpen(false)
                      }}
                      className="w-full text-left mb-3 px-4 py-1 rounded-md flex items-center gap-2 text-slate-700 hover:bg-sky-500 hover:text-white"
                    >
                      <CalendarDays size={16} className="shrink-0" />
                      {!getIsTabletOnly() && (
                        <span className="flex-grow truncate" title={getDigestDisplayTitle(digest, isoDate)}>
                          {getDigestDisplayTitle(digest, isoDate)}
                        </span>
                      )}
                    </Link>
                  </li>
                ))}
              </ul>
            </nav>
          )}
        </div>

        <div className="flex flex-col w-full p-4 border-b gap-1 border-b-slate-300">
          <button onClick={() => {
            handleViewAllTasksClick()
          }}
            className='flex flex-row justify-between items-center w-full p-2 m-0 gap-4 rounded-lg bg-transparent text-slate-700 hover:bg-sky-500 hover:text-white'>
            <Link className='flex items-center flex-row justify-between w-full text-left rounded-md hover:shadow-sm' 
              to='/tasks'
              data-amp-track-section="Sidebar"
              data-amp-track-action="View_All_Tasks_Click"
              data-amp-track-description="View All Tasks"
            >
              <div className={`flex flex-row font-semibold gap-4 w-full items-center ${!isMenuOpen && (getIsTabletOnly() || !isSidebarOpen) ? 'justify-center' : 'justify-start'}`}>
                <ListChecks size={24} />
                {isMenuOpen ? 'Tasks' : (!getIsTabletOnly() && isSidebarOpen) ? 'Tasks' : ''}
              </div>
              
              <div className={`flex flex-row justify-center items-center gap-2`}>
                <span className="text-sm truncate">
                  {isMenuOpen ? 'View All' : (!getIsTabletOnly() && isSidebarOpen) ? 'View All' : ''}
                </span>
              </div>
            </Link>
          </button>

          {(getIsMobileOnly() || (!getIsTabletOnly() && isSidebarOpen)) && tasks.length > 0 && (
            <nav className="">
              <ul className="mt-2 px-2">
                <li>
                  <Link
                    to={`/task/category/one-thing-now`}
                    data-amp-track-section="Sidebar"
                    data-amp-track-action="View_Task_Category_Click"
                    data-amp-track-description="View One Thing Now Category"
                    className="w-full text-left mb-3 px-4 py-1 rounded-md flex items-center gap-2 text-slate-700 hover:bg-sky-500 hover:text-white"
                  >
                    <TaskCategoryIcon slug="one-thing-now" />
                    {!getIsTabletOnly() && (
                      <span className="flex-grow truncate" title="One Thing Now">One Thing Now</span>
                    )}
                  </Link>
                </li>

                {projectCategories.filter(cat => cat.slug !== 'one-thing-now').slice(0, fullTaskListVisible ? undefined : 3).map((cat) => (
                  <li key={cat.id}>
                    <Link
                      to={`/task/category/${cat.slug}`}
                      data-amp-track-section="Sidebar"
                      data-amp-track-action="View_Task_Category_Click"
                      data-amp-track-description={`View ${cat.title} Category`}
                      className="w-full text-left mb-3 px-4 py-1 rounded-md flex items-center gap-2 text-slate-700 hover:bg-sky-500 hover:text-white"
                    >
                      <TaskCategoryIcon slug={cat.slug} />
                      {!getIsTabletOnly() && (
                        <span className="flex-grow truncate" title={cat.title}>{cat.title}</span>
                      )}
                    </Link>
                  </li>
                ))}

                {!fullTaskListVisible && (
                  <li>
                  <button onClick={() => setFullTaskListVisible(!fullTaskListVisible)} 
                    data-amp-track-section="Sidebar"
                    data-amp-track-action="View_All_Task_Categories_Click"
                    data-amp-track-description="View All Task Categories"
                    className="w-full ml-0 text-left mb-3 px-4 py-1 rounded-md flex items-center gap-2 bg-transparent text-slate-700 hover:bg-sky-500 hover:text-white"
                  >
                    <span className="flex-grow truncate">Show All Categories</span>
                    </button>
                  </li>
                )}
              </ul>
            </nav>
          )}

          <button 
            data-amp-track-section="Sidebar"
            data-amp-track-action="Add_Task_Click"
            data-amp-track-description="Add New Task"
            onClick={handleCreateNewTask}
            className={`flex items-center p-2 m-0 gap-4 bg-transparent font-semibold text-sky-600 hover:bg-sky-500 hover:text-white rounded-lg ${!isMenuOpen && (getIsTabletOnly() || !isSidebarOpen) ? 'justify-center' : 'justify-start'}`}>
            <Plus size={24}/> 
            {isMenuOpen ? 'Add Task' : (!getIsTabletOnly() && isSidebarOpen) ? 'Add Task' : ''}
          </button>
        </div>

        <div className="mt-auto p-4 flex flex-col gap-2">
          {isAdmin && (
            <Link
              to="/admin/response-partners"
              onClick={() => {
                if (getIsMobileOnly()) setIsMenuOpen(false)
              }}
              data-amp-track-section="Sidebar"
              data-amp-track-action="Admin_Response_Partners_Click"
              data-amp-track-description="Open Response Partners Admin"
              title="Response Partners (Admin)"
              className={`flex flex-row items-center font-semibold text-stone-700 ${!isMenuOpen && (getIsTabletOnly() || !isSidebarOpen) ? 'justify-center' : 'justify-start'} p-2 m-0 gap-3 rounded-md bg-transparent hover:bg-sky-500 hover:text-white`}
            >
              <Shield size={22} />
              <span className={`${!isMenuOpen && (getIsTabletOnly() || !isSidebarOpen) ? 'sr-only' : ''}`}>
                Response Partners
              </span>
            </Link>
          )}
          <div className="flex items-center justify-around">
            <nav>
              <SignedIn>
                <UserButton />
              </SignedIn>

              <SignedOut>
                <RedirectToSignIn />
              </SignedOut>
            </nav>
            {/* {!getIsTabletOnly() && <PopinSiteMenu />} */}
          </div>
        </div>
      </aside>
    )
  }

  // For mobile: render hamburger menu
  if (getIsMobileOnly()) {
    return (
      <div className="sticky top-0 z-50 bg-slate-200 text-gray-800">
        <div className="sticky top-0 z-50 flex flex-row items-center justify-start py-2 px-4 border-b">
          <button 
            onClick={toggleMobileMenu}
            data-amp-track-section="Sidebar"
            data-amp-track-action="Mobile_Menu_Click"
            data-amp-track-description={isMenuOpen ? 'Close Mobile Menu' : 'Open Mobile Menu'}
            className="flex flex-col items-center justify-center gap-3 text-black bg-transparent p-0 mt-2 mr-4"
          >
            <Menu size={28} />
          </button>
          
          <div className={`fixed inset-0 bg-black bg-opacity-50 z-40 transition-opacity duration-300 ${isMenuOpen ? 'opacity-100' : 'opacity-0 pointer-events-none'}`} 
            data-amp-track-section="Sidebar"
            data-amp-track-action="Mobile_Menu_Overlay_Click"
            data-amp-track-description="Close Mobile Menu"
            onClick={() => setIsMenuOpen(false)}
          />

          <div className='flex flex-row items-center justify-start gap-3'>
            <PrinceGeoffrey size={28} className='justify-center items-center m-0 p-0' />
            <h1 className="text-base text-slate-700 font-bold mt-2">Geoffrey AI</h1>
          </div>
        </div>
              
        <div className={`fixed top-0 left-0 flex h-screen bg-slate-200 z-50 w-64 transform transition-transform duration-300 ease-in-out shadow-xl ${isMenuOpen ? 'translate-x-0' : '-translate-x-full'}`}>
          {RenderSideMenu()}
        </div>

        {showTaskSetup && pendingTask && (
          <TaskSetupModal 
            task={pendingTask} 
            onUpdateTask={handleTaskUpdate} 
            onCancelTask={handleCancelTask} 
            onSaveTask={handleSaveTask} 
          />
        )}
      </div>
    )
  }

  // For tablet and desktop view
  return (
    <div className={`flex h-screen bg-white border-r transition-all duration-300 ease-in-out ${windowWidth >= 1024 ? (isSidebarOpen ? 'w-72' : 'w-20') : (isSidebarOpen ? 'w-20' : 'w-20')}`}>
        
      <div className="flex w-full bg-slate-200 text-gray-800">
        {RenderSideMenu()}
      </div>

      {showTaskSetup && pendingTask && (
        <TaskSetupModal 
          task={pendingTask} 
          onUpdateTask={handleTaskUpdate} 
          onCancelTask={handleCancelTask} 
          onSaveTask={handleSaveTask} 
        />
      )}
    </div>
  ) 
}
